export type ParametricFieldType = "number" | "choice" | "boolean";

export type ParametricOption = {
  value: string;
  label: string;
};

export type ParametricField = {
  key: string;
  label: string;
  type: ParametricFieldType;
  unit?: string;
  required?: boolean;
  min?: number;
  max?: number;
  options?: ParametricOption[];
  help?: string;
};

export type ReinforcedConcreteWorkUnit = {
  code: string;
  family: "foundation" | "vertical" | "horizontal" | "other";
  name: string;
  unit: "m3";
  description: string;
  fields: ParametricField[];
};

const concreteStrengthOptions: ParametricOption[] = [
  { value: "C20/25", label: "C20/25" },
  { value: "C25/30", label: "C25/30" },
  { value: "C30/37", label: "C30/37" },
  { value: "C35/45", label: "C35/45" },
  { value: "C40/50", label: "C40/50" },
  { value: "C45/55", label: "C45/55" },
];

const exposureOptions: ParametricOption[] = [
  { value: "X0", label: "X0" },
  { value: "XC1", label: "XC1" },
  { value: "XC2", label: "XC2" },
  { value: "XC3", label: "XC3" },
  { value: "XC4", label: "XC4" },
  { value: "XD", label: "XD — cloretos não marinhos" },
  { value: "XS", label: "XS — ambiente marinho" },
  { value: "XF", label: "XF — gelo/degelo" },
  { value: "XA", label: "XA — ataque químico" },
];

const placementOptions: ParametricOption[] = [
  { value: "pump", label: "Bomba" },
  { value: "crane", label: "Grua / balde" },
  { value: "direct", label: "Descarga direta" },
  { value: "other", label: "Outro" },
];

const steelOptions: ParametricOption[] = [
  { value: "A500NR", label: "A500 NR" },
  { value: "A500ER", label: "A500 ER" },
  { value: "A400NR", label: "A400 NR" },
  { value: "other", label: "Outro" },
];

const commonConcreteFields: ParametricField[] = [
  {
    key: "concrete_strength",
    label: "Classe do betão",
    type: "choice",
    required: true,
    options: concreteStrengthOptions,
  },
  {
    key: "exposure_class",
    label: "Classe de exposição",
    type: "choice",
    required: true,
    options: exposureOptions,
  },
  {
    key: "placement_method",
    label: "Betonagem",
    type: "choice",
    required: true,
    options: placementOptions,
  },
  {
    key: "steel_grade",
    label: "Aço",
    type: "choice",
    required: true,
    options: steelOptions,
  },
  {
    key: "reinforcement_kg_m3",
    label: "Aço por m³",
    type: "number",
    unit: "kg/m³",
    required: true,
    min: 0,
    help: "Quantidade efetiva ou estimada de armadura por m³ de betão.",
  },
  {
    key: "concrete_excess_pct",
    label: "Excesso / desperdício de betão",
    type: "number",
    unit: "%",
    min: 0,
  },
  {
    key: "steel_waste_pct",
    label: "Desperdício de aço",
    type: "number",
    unit: "%",
    min: 0,
  },
];

const formworkField: ParametricField = {
  key: "formwork_system",
  label: "Sistema de cofragem",
  type: "choice",
  required: true,
  options: [
    { value: "recoverable_metal", label: "Recuperável metálica" },
    { value: "recoverable_wood", label: "Recuperável madeira/painel" },
    { value: "lost", label: "Cofragem perdida" },
    { value: "none", label: "Sem cofragem" },
    { value: "other", label: "Outro" },
  ],
};

const formworkRatioField: ParametricField = {
  key: "formwork_ratio_m2_m3",
  label: "Cofragem por m³",
  type: "number",
  unit: "m²/m³",
  min: 0,
  help: "Pode ser calculada geometricamente ou confirmada/ajustada pelo orçamentista.",
};

const formworkReuseField: ParametricField = {
  key: "formwork_reuses",
  label: "Reutilizações previstas",
  type: "number",
  unit: "utilizações",
  min: 1,
};

const executionDifficultyField: ParametricField = {
  key: "execution_complexity",
  label: "Complexidade de execução",
  type: "choice",
  required: true,
  options: [
    { value: "simple", label: "Simples" },
    { value: "normal", label: "Normal" },
    { value: "complex", label: "Complexa" },
  ],
};

export const reinforcedConcreteWorkUnits: ReinforcedConcreteWorkUnit[] = [
  {
    code: "BA.FND.SAPATA.ISOLADA",
    family: "foundation",
    name: "Sapata isolada de betão armado",
    unit: "m3",
    description: "Sapata isolada, incluindo betão, armaduras e cofragem quando aplicável.",
    fields: [
      ...commonConcreteFields,
      { key: "length", label: "Comprimento", type: "number", unit: "m", required: true, min: 0 },
      { key: "width", label: "Largura", type: "number", unit: "m", required: true, min: 0 },
      { key: "height", label: "Altura", type: "number", unit: "m", required: true, min: 0 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.FND.SAPATA.CORRIDA",
    family: "foundation",
    name: "Sapata corrida de betão armado",
    unit: "m3",
    description: "Sapata corrida, incluindo betão, armaduras e cofragem quando aplicável.",
    fields: [
      ...commonConcreteFields,
      { key: "width", label: "Largura", type: "number", unit: "m", required: true, min: 0 },
      { key: "height", label: "Altura", type: "number", unit: "m", required: true, min: 0 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.FND.MACICO",
    family: "foundation",
    name: "Maciço / encepamento de betão armado",
    unit: "m3",
    description: "Maciço ou encepamento de betão armado.",
    fields: [
      ...commonConcreteFields,
      { key: "formwork_faces", label: "Faces cofradas", type: "number", unit: "nº", min: 0 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.FND.VIGA",
    family: "foundation",
    name: "Viga de fundação de betão armado",
    unit: "m3",
    description: "Viga de fundação, incluindo betão, armaduras e cofragem.",
    fields: [
      ...commonConcreteFields,
      { key: "width", label: "Largura", type: "number", unit: "m", required: true, min: 0 },
      { key: "height", label: "Altura", type: "number", unit: "m", required: true, min: 0 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.VERT.PILAR",
    family: "vertical",
    name: "Pilar de betão armado",
    unit: "m3",
    description: "Pilar de betão armado com armaduras, betonagem e cofragem.",
    fields: [
      ...commonConcreteFields,
      { key: "section_a", label: "Secção A", type: "number", unit: "m", required: true, min: 0 },
      { key: "section_b", label: "Secção B", type: "number", unit: "m", required: true, min: 0 },
      { key: "storey_height", label: "Altura", type: "number", unit: "m", required: true, min: 0 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.VERT.MURO",
    family: "vertical",
    name: "Muro de betão armado",
    unit: "m3",
    description: "Muro de betão armado com armaduras e cofragem a uma ou duas faces.",
    fields: [
      ...commonConcreteFields,
      { key: "thickness", label: "Espessura", type: "number", unit: "m", required: true, min: 0 },
      { key: "height", label: "Altura", type: "number", unit: "m", required: true, min: 0 },
      {
        key: "formwork_faces",
        label: "Cofragem",
        type: "choice",
        required: true,
        options: [
          { value: "one", label: "Uma face" },
          { value: "two", label: "Duas faces" },
        ],
      },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.HOR.VIGA",
    family: "horizontal",
    name: "Viga de betão armado",
    unit: "m3",
    description: "Viga de betão armado com armaduras, cofragem e escoramento.",
    fields: [
      ...commonConcreteFields,
      { key: "width", label: "Largura", type: "number", unit: "m", required: true, min: 0 },
      { key: "height", label: "Altura", type: "number", unit: "m", required: true, min: 0 },
      { key: "execution_height", label: "Altura de execução", type: "number", unit: "m", required: true, min: 0 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.HOR.LAJE.MACICA",
    family: "horizontal",
    name: "Laje maciça de betão armado",
    unit: "m3",
    description: "Laje maciça com armaduras, cofragem e escoramento.",
    fields: [
      ...commonConcreteFields,
      { key: "thickness", label: "Espessura", type: "number", unit: "m", required: true, min: 0 },
      { key: "execution_height", label: "Altura de execução", type: "number", unit: "m", required: true, min: 0 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.HOR.LAJE.ALIGEIRADA",
    family: "horizontal",
    name: "Laje aligeirada de betão armado",
    unit: "m3",
    description: "Laje aligeirada com nervuras, elementos de aligeiramento, armaduras e capa de compressão.",
    fields: [
      ...commonConcreteFields,
      { key: "total_thickness", label: "Espessura total", type: "number", unit: "m", required: true, min: 0 },
      { key: "compression_layer", label: "Capa de compressão", type: "number", unit: "m", required: true, min: 0 },
      { key: "execution_height", label: "Altura de execução", type: "number", unit: "m", required: true, min: 0 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
  {
    code: "BA.OTH.ESCADA",
    family: "other",
    name: "Escada de betão armado",
    unit: "m3",
    description: "Escada de betão armado, incluindo lanços, patamares, armaduras e cofragem.",
    fields: [
      ...commonConcreteFields,
      { key: "waist_thickness", label: "Espessura da laje/lanço", type: "number", unit: "m", required: true, min: 0 },
      { key: "width", label: "Largura", type: "number", unit: "m", required: true, min: 0 },
      { key: "flight_count", label: "Nº de lanços", type: "number", unit: "nº", required: true, min: 1 },
      formworkField,
      formworkRatioField,
      formworkReuseField,
      executionDifficultyField,
    ],
  },
];

export const reinforcedConcreteResourceGroups = [
  {
    key: "concrete",
    label: "Betão",
    examples: ["C25/30", "C30/37", "C35/45"],
  },
  {
    key: "reinforcement",
    label: "Armaduras",
    examples: ["A500 NR", "A500 ER", "malhas", "arame", "separadores"],
  },
  {
    key: "formwork",
    label: "Cofragem e escoramento",
    examples: ["painéis", "escoras", "descofrante", "acessórios"],
  },
  {
    key: "labor",
    label: "Mão de obra",
    examples: ["armador", "cofrador", "estruturista", "ajudantes"],
  },
  {
    key: "equipment",
    label: "Equipamentos",
    examples: ["bomba", "grua", "vibrador", "corte/dobragem"],
  },
] as const;
